import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Home as HomeIcon,
  MessageCircle,
  Users,
  Sparkles,
  Store,
  FileText,
  Package,
  Tag,
  Truck,
} from "lucide-react";
import { SERVICES, whatsappUrl } from "@/lib/site-data";
import { SERVICE_META } from "@/lib/service-visuals";
import { SERVICE_EXTRAS } from "@/lib/service-extras";
import useSeo from "@/lib/useSeo";
import JsonLd from "@/lib/JsonLd";
import ServiceWorkflow from "@/components/ServiceWorkflow";

const SLUG = "pazaryeri-kurulum-yonetimi";

/* Code-native marketplace-operation flow panel — replaces the previous
   generic hero illustration with a professional operational-status card. */
function OpsFlowPanel() {
  const rows = [
    { title: "Kanal seçimi", desc: "Sektöre uygun pazaryerleri", state: "done" },
    { title: "Başvuru hazırlanıyor", desc: "Evrak ve mağaza kaydı", state: "done" },
    { title: "Ürünler yükleniyor", desc: "Katalog ve görseller", state: "active" },
    { title: "Mağaza açıldı", desc: "Kanallarda yayın", state: "pending" },
    { title: "Operasyon takip ediliyor", desc: "Sipariş • kampanya • stok", state: "pending" },
  ];
  return (
    <div
      className="ep-ops-panel hidden lg:block"
      data-testid="pazaryeri-ops-panel"
      aria-hidden="true"
    >
      <div className="ep-ops-panel-head">
        <div className="flex items-center gap-2">
          <span className="ep-ops-dot" />
          <span className="ep-ops-dot ep-ops-dot-y" />
          <span className="ep-ops-dot ep-ops-dot-g" />
        </div>
        <span className="ep-ops-panel-title">İşyeri Operasyonu • Canlı</span>
        <span className="ep-ops-live">
          <span className="ep-ops-live-dot" />
          Yayın
        </span>
      </div>
      <ol className="ep-ops-list">
        {rows.map((r, i) => (
          <li
            key={i}
            className={`ep-ops-row ep-ops-row-${r.state} ep-ops-row-anim`}
            style={{ animationDelay: `${180 + i * 110}ms` }}
          >
            <span className="ep-ops-index">0{i + 1}</span>
            <span className="ep-ops-connector" aria-hidden="true">
              <span className="ep-ops-connector-pulse" />
            </span>
            <div className="ep-ops-body">
              <div className="ep-ops-row-title">{r.title}</div>
              <div className="ep-ops-row-desc">{r.desc}</div>
            </div>
            <span className={`ep-ops-status ep-ops-status-${r.state}`}>
              {r.state === "done" && "Tamam"}
              {r.state === "active" && (
                <>
                  Sürüyor
                  <span className="ep-ops-status-bar" aria-hidden="true">
                    <span className="ep-ops-status-bar-fill" />
                  </span>
                </>
              )}
              {r.state === "pending" && "Sırada"}
            </span>
          </li>
        ))}
      </ol>
      <div className="ep-ops-foot">
        <span>Süreç: 5 aşama</span>
        <span>Ekip: Operasyon</span>
      </div>
    </div>
  );
}

const STAGES = [
  {
    n: "01",
    title: "Analiz ve Kanal Seçimi",
    desc: "İşyerinizin sektörünü, ürün yapısını ve teslimat kapasitesini inceleyerek uygun satış kanallarını belirliyoruz.",
  },
  {
    n: "02",
    title: "Başvuru ve Mağaza Açılışı",
    desc: "Gerekli evrakları ve başvuru sürecini takip ediyor, mağaza açılışına kadar süreci yönetiyoruz.",
  },
  {
    n: "03",
    title: "Ürün ve Menü Kurulumu",
    desc: "Ürün adı, açıklama, kategori, barkod, KDV, fiyat ve görselleri satışa hazır hâle getiriyoruz.",
  },
  {
    n: "04",
    title: "Günlük Operasyon Yönetimi",
    desc: "Sipariş, kampanya, fiyat-stok, fatura, iptal-iade ve mağaza performansını düzenli olarak takip ediyoruz.",
  },
];

const CHANNELS = [
  "Uber Eats Trendyol Go",
  "Uber Eats Trendyol Go Randevulu",
  "Getir Çarşı",
  "Getir Yemek",
  "Yemeksepeti Mahalle",
  "Yemeksepeti",
  "Migros Yemek",
  "Sektöre uygun diğer pazaryerleri",
];

/* Regroup existing `scope` (from site-data) into 4 clean buckets without
   dropping any item. All 15 items appear exactly once. */
const SCOPE_GROUPS = [
  {
    title: "Başvuru ve Mağaza Açılışı",
    Icon: FileText,
    items: [
      "İşyeri için uygun pazaryerlerinin belirlenmesi",
      "Mağaza başvurusu",
      "Gerekli evrak süreçlerinin takibi",
      "Mağaza açılışı",
    ],
  },
  {
    title: "Ürün ve Menü Hazırlığı",
    Icon: Package,
    items: [
      "Ürün veya menü listesinin hazırlanması",
      "Ürün adı ve açıklamalarının düzenlenmesi",
      "Kategori, marka, barkod, gramaj ve KDV bilgilerinin hazırlanması",
      "Ürün ve menü görsellerinin düzenlenmesi",
      "Seçenek ve ek ürün gruplarının oluşturulması",
    ],
  },
  {
    title: "Fiyat, Stok ve Kampanya",
    Icon: Tag,
    items: ["Fiyat ve stok kontrolü", "Kampanya ve reklam takibi"],
  },
  {
    title: "Sipariş ve Operasyon Takibi",
    Icon: Truck,
    items: [
      "Sipariş operasyonu",
      "İptal ve iade takibi",
      "Fatura süreçlerinin takibi",
      "Mağaza performansının incelenmesi",
    ],
  },
];

export default function PazaryeriDetail() {
  const svc = SERVICES.find((s) => s.slug === SLUG);
  const meta = SERVICE_META[SLUG];
  const extras = SERVICE_EXTRAS[SLUG] || {};
  const related = SERVICES.filter((s) => s.slug !== SLUG);

  useSeo({
    title: svc?.seoTitle,
    description: svc?.seoDescription,
  });

  return (
    <>
      <JsonLd
        id={`service-detail-breadcrumb-${SLUG}`}
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://epersonel.biz/" },
            { "@type": "ListItem", position: 2, name: "Hizmetlerimiz", item: "https://epersonel.biz/hizmetlerimiz" },
            { "@type": "ListItem", position: 3, name: svc.title, item: `https://epersonel.biz/hizmetlerimiz/${SLUG}` },
          ],
        }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-10 md:pt-14 pb-8 md:pb-12">
        <div className="ep-glow-tl" aria-hidden="true" />
        <div className="ep-container relative">
          <nav aria-label="Sayfa Yolu" className="flex items-center gap-2 text-xs text-white/60 mb-6 md:mb-8">
            <Link to="/" className="hover:text-[#00F22E] inline-flex items-center gap-1">
              <HomeIcon className="h-3 w-3" /> Ana Sayfa
            </Link>
            <span>/</span>
            <Link to="/hizmetlerimiz" className="hover:text-[#00F22E]">Hizmetlerimiz</Link>
            <span>/</span>
            <span className="text-white/80">{svc.title}</span>
          </nav>

          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,480px)] gap-10 lg:gap-14 items-center">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-[#00F22E]/10 text-[#00F22E] grid place-items-center shrink-0 ring-1 ring-[#00F22E]/25">
                  <Store className="h-5 w-5" />
                </div>
                <span className="text-[10px] tracking-[0.3em] text-[#00F22E]/90 font-semibold">
                  {meta.category}
                </span>
              </div>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08]">
                Pazaryeri Mağazanızı Kuruyor, Günlük{" "}
                <span className="text-[#00F22E]">Operasyonunuzu Yönetiyoruz.</span>
              </h1>
              <p className="mt-6 text-white/85 text-base md:text-lg leading-relaxed max-w-xl">
                Mağaza başvurusundan binlerce ürünün hazırlanmasına, fiyat ve
                stok güncellemelerinden kampanya ve sipariş takibine kadar zaman
                alan pazaryeri süreçlerini Epersonel&apos;e bırakın.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/basvuru" data-testid="pazaryeri-hero-cta-primary" className="btn-primary">
                  Ücretsiz Analiz İste <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={whatsappUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pazaryeri-hero-cta-whatsapp"
                  className="btn-secondary"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp&apos;tan Görüşelim
                </a>
              </div>
            </div>
            <OpsFlowPanel />
          </div>
        </div>
      </section>

      {/* AUDIENCE */}
      {extras.audience && (
        <section className="py-12 md:py-16">
          <div className="ep-container">
            <div className="max-w-3xl mb-8 md:mb-10">
              <p className="text-xs tracking-[0.3em] text-[#00F22E]/90 font-semibold mb-3">UYUM</p>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                Bu Hizmet <span className="text-[#00F22E]">Kimler İçin Uygun?</span>
              </h2>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {extras.audience.map((a, i) => (
                <li key={i} className="ep-card p-5 flex items-start gap-3 h-full">
                  <Users className="h-5 w-5 text-[#00F22E] mt-0.5 shrink-0" />
                  <span className="text-sm md:text-[15px] text-white/90 leading-relaxed">{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* WORKFLOW — code-native animated pipeline */}
      <ServiceWorkflow slug={SLUG} />

      {/* 4-STAGE PROCESS */}
      <section className="py-14 md:py-20 ep-section-tier-2 border-y border-white/[0.05] relative overflow-hidden">
        <div className="ep-glow-br" aria-hidden="true" />
        <div className="ep-container relative">
          <div className="max-w-3xl mb-10">
            <p className="text-xs tracking-[0.3em] text-[#00F22E]/90 font-semibold mb-3">SÜREÇ</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              Pazaryeri Operasyonunu{" "}
              <span className="text-[#00F22E]">Dört Aşamada</span> Yönetiyoruz
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="pazaryeri-4-stages">
            {STAGES.map((s, i) => (
              <div key={i} className="ep-pz-stage h-full flex flex-col" style={{ animationDelay: `${i * 90}ms` }}>
                <span className="ep-pz-stage-num">{s.n}</span>
                <h3 className="ep-pz-stage-title">{s.title}</h3>
                <p className="ep-pz-stage-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARKETPLACE CHANNELS */}
      <section className="py-14 md:py-20">
        <div className="ep-container">
          <div className="max-w-3xl mb-8 md:mb-10">
            <p className="text-xs tracking-[0.3em] text-[#00F22E]/90 font-semibold mb-3">KANAL</p>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
              İşyerinize <span className="text-[#00F22E]">Uygun Satış Kanalları</span>
            </h2>
            <p className="mt-4 text-white/80 text-base md:text-lg leading-relaxed">
              Sektörünüze ve operasyon yapınıza göre uygun pazaryerlerini
              birlikte belirliyoruz.
            </p>
          </div>
          <ul
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4"
            data-testid="pazaryeri-channels"
          >
            {CHANNELS.map((c, i) => {
              const isRandevu = c.includes("Randevulu");
              return (
                <li
                  key={i}
                  className="ep-pz-channel h-full min-h-[92px] flex items-center justify-center text-center relative"
                >
                  {isRandevu && (
                    <span className="absolute top-2 right-2 text-[9px] tracking-[0.24em] font-semibold text-[#00F22E] bg-[#00F22E]/10 border border-[#00F22E]/35 rounded-full px-2 py-0.5">
                      RANDEVULU
                    </span>
                  )}
                  <span className="text-sm md:text-[15px] font-medium text-white/90 px-2">
                    {c}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="mt-6 text-xs text-white/60 leading-relaxed max-w-3xl">
            Epersonel pazaryeri ortağı değil, Üye İşyeri operasyon partneridir.
            Marka ve pazaryeri adları yalnızca bilgilendirme amacıyla yer alır ve
            ilgili sahiplerine aittir.
          </p>
        </div>
      </section>

      {/* SCOPE — reorganized into 4 groups */}
      <section className="py-14 md:py-20 ep-section-tier-2 border-y border-white/[0.05] relative overflow-hidden">
        <div className="ep-glow-tl" aria-hidden="true" />
        <div className="ep-container relative">
          <div className="max-w-3xl mb-10">
            <p className="text-xs tracking-[0.3em] text-[#00F22E]/90 font-semibold mb-3">KAPSAM</p>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
              Hizmet <span className="text-[#00F22E]">Kapsamı</span>
            </h2>
          </div>
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-5"
            data-testid="pazaryeri-scope-groups"
          >
            {SCOPE_GROUPS.map((g, i) => {
              const Icon = g.Icon;
              return (
                <div key={i} className="ep-card p-6 h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-[#00F22E]/10 text-[#00F22E] grid place-items-center ring-1 ring-[#00F22E]/25 shrink-0">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-display font-semibold text-lg leading-snug">
                      {g.title}
                    </h3>
                  </div>
                  <ul className="space-y-2.5">
                    {g.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm md:text-[15px] text-white/85 leading-relaxed">
                        <CheckCircle2 className="h-4 w-4 text-[#00F22E] mt-1 shrink-0" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      {extras.benefits && (
        <section className="py-14 md:py-20">
          <div className="ep-container">
            <div className="max-w-3xl mb-8 md:mb-10">
              <p className="text-xs tracking-[0.3em] text-[#00F22E]/90 font-semibold mb-3">KAZANIM</p>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                Bu Hizmetle <span className="text-[#00F22E]">Neler Kazanırsınız?</span>
              </h2>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {extras.benefits.map((b, i) => (
                <li key={i} className="ep-card p-5 flex items-start gap-3 h-full">
                  <Sparkles className="h-5 w-5 text-[#00F22E] mt-0.5 shrink-0" />
                  <span className="text-sm md:text-[15px] text-white/90 leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* RELATED SERVICES */}
      {related.length > 0 && (
        <section className="py-14 md:py-20 ep-section-tier-2 border-y border-white/[0.05]">
          <div className="ep-container">
            <div className="max-w-3xl mb-10">
              <p className="text-xs tracking-[0.3em] text-[#00F22E]/90 font-semibold mb-3">DEVAMI VAR</p>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                İlgili Diğer <span className="text-[#00F22E]">Hizmetler</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((r) => {
                const rMeta = SERVICE_META[r.slug];
                const RIcon = rMeta?.Icon || Store;
                return (
                  <Link
                    key={r.slug}
                    to={`/hizmetlerimiz/${r.slug}`}
                    data-testid={`related-service-${r.slug}`}
                    className="ep-svc-card group flex flex-col h-full"
                    style={{ "--svc-accent": rMeta?.accent || "#00F22E" }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-[#00F22E]/10 text-[#00F22E] grid place-items-center shrink-0 ring-1 ring-[#00F22E]/25">
                        <RIcon className="h-5 w-5" />
                      </div>
                      <span className="text-[10px] tracking-[0.24em] text-[#00F22E]/80 font-semibold">
                        {rMeta?.category || "HİZMET"}
                      </span>
                    </div>
                    <h3 className="font-display text-lg font-semibold tracking-tight leading-snug">
                      {r.title}
                    </h3>
                    <p className="mt-3 text-sm text-white/70 leading-relaxed flex-1">{r.short}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#00F22E] font-medium group-hover:gap-2.5 transition-[gap] duration-200">
                      {r.ctaLabel} <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CLOSING CTA */}
      <section className="py-12 md:py-16 relative overflow-hidden">
        <div className="ep-glow-tl" aria-hidden="true" />
        <div className="ep-glow-br" aria-hidden="true" />
        <div className="ep-container relative">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              Pazaryerlerinde Satışa Başlamaya{" "}
              <span className="text-[#00F22E]">Hazır mısınız?</span>
            </h2>
            <p className="mt-5 text-white/80 text-base md:text-lg leading-relaxed">
              Mevcut yapınızı ve hedeflerinizi inceleyerek işyerinize özel bir yol
              haritası oluşturalım.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/basvuru" className="btn-primary" data-testid="pazaryeri-cta-primary">
                Ücretsiz Analiz İste <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={whatsappUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
                data-testid="pazaryeri-cta-whatsapp"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp&apos;tan Görüşelim
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
